import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, Sparkles, Check, Plus, Minus, Trash2, Clock, Truck, Store, Palette } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { HOME, BOOKING } from "@/constants/testIds";
import { resolveImageUrl } from "@/components/ImageUploader";
import ImageUploader from "@/components/ImageUploader";

const EVENT_TYPES = [
  "Birthday", "Wedding", "Baby Shower", "Bridal Shower",
  "Anniversary", "Corporate Event", "Graduation", "Holiday Party", "Other",
];

const clampQty = (n) => Math.max(1, Math.min(8, Math.round(n || 1)));
const money = (n) => `$${(n || 0).toFixed(2)}`;
const flavorKey = (f) => (typeof f === "string" ? f : f?.name || "");
const flavorPrice = (f) => (typeof f === "string" ? 0 : (f?.price_per_dozen || 0));
const flavorNotice = (f) => (typeof f === "string" ? "" : (f?.notice || ""));

export default function BookingForm({ config }) {
  const c = config?.content || {};
  const DESIGN_FEE = config?.design_fee ?? 20;
  const DELIVERY_FEE = config?.delivery_fee ?? 30;
  const DELIVERY_MAX_MILES = config?.delivery_max_miles ?? 15;
  const [form, setForm] = useState({
    name: "", email: "", phone: "", event_type: "", guest_count: 20, notes: "",
  });
  const [date, setDate] = useState(null);
  const [menu, setMenu] = useState([]);
  // Map<menu_item_id, { menu_item_name, flavors: {flavor: quantity} }>
  const [selection, setSelection] = useState({});
  const [fulfillment, setFulfillment] = useState("pickup"); // 'pickup' | 'delivery'
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [includesDesign, setIncludesDesign] = useState(false);
  const [designGallery, setDesignGallery] = useState([]);
  const [designReferenceUrl, setDesignReferenceUrl] = useState("");
  const [uploadingInspiration, setUploadingInspiration] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [blockedSet, setBlockedSet] = useState(() => new Set());
  const [blockedReasons, setBlockedReasons] = useState(() => ({}));

  const dateDisabled = useMemo(() => (d) => {
    const tomorrow = new Date(); tomorrow.setHours(0, 0, 0, 0); tomorrow.setDate(tomorrow.getDate() + 1);
    if (d < tomorrow) return true;
    const iso = format(d, "yyyy-MM-dd");
    return blockedSet.has(iso);
  }, [blockedSet]);

  const dateComponents = useMemo(() => ({
    DayContent: ({ date: d }) => {
      const iso = format(d, "yyyy-MM-dd");
      const reason = blockedReasons[iso];
      const title = reason ? `Unavailable — ${reason}` : (blockedSet.has(iso) ? "Unavailable" : "");
      return <span title={title}>{d.getDate()}</span>;
    },
  }), [blockedReasons, blockedSet]);

  useEffect(() => {
    api.get("/menu").then((r) => setMenu(r.data || [])).catch((e) => console.error("menu load failed", e));
    api.get("/blocked-dates").then((r) => {
      setBlockedSet(new Set(r.data?.dates || []));
      setBlockedReasons(r.data?.reasons || {});
    }).catch((e) => console.error("blocked-dates load failed", e));
    api.get("/design-gallery").then((r) => setDesignGallery(r.data?.images || [])).catch((e) => console.error("design-gallery load failed", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleFlavor = (item, flavorObj) => {
    const name = flavorKey(flavorObj);
    if (!name) return;
    setSelection((s) => {
      const current = s[item.id] || { menu_item_name: item.name, flavors: {} };
      const flavors = { ...current.flavors };
      if (flavors[name]) delete flavors[name];
      else flavors[name] = { quantity: 1, price_per_dozen: flavorPrice(flavorObj) };
      const next = { ...current, menu_item_name: item.name, flavors };
      const cleaned = { ...s };
      if (Object.keys(next.flavors).length === 0) delete cleaned[item.id];
      else cleaned[item.id] = next;
      return cleaned;
    });
    setErrors((e) => ({ ...e, [item.id]: null, form: null }));
  };

  const setFlavorQty = (item, flavor, qty) => {
    setSelection((s) => {
      const current = s[item.id];
      if (!current || !current.flavors[flavor]) return s;
      return {
        ...s,
        [item.id]: {
          ...current,
          flavors: {
            ...current.flavors,
            [flavor]: { ...current.flavors[flavor], quantity: clampQty(qty) },
          },
        },
      };
    });
  };

  const removeItem = (id) => setSelection((s) => { const n = { ...s }; delete n[id]; return n; });

  const buildItemsPayload = () =>
    Object.entries(selection).map(([id, sel]) => ({
      menu_item_id: id,
      menu_item_name: sel.menu_item_name,
      flavors: Object.entries(sel.flavors).map(([flavor, meta]) => ({
        flavor,
        quantity: meta.quantity,
        price_per_dozen: meta.price_per_dozen || 0,
      })),
    }));

  const totalDozens = Object.values(selection).reduce(
    (sum, sel) => sum + Object.values(sel.flavors).reduce((a, m) => a + m.quantity, 0),
    0
  );
  const treatsTotal = Object.values(selection).reduce(
    (sum, sel) => sum + Object.values(sel.flavors).reduce((a, m) => a + m.quantity * (m.price_per_dozen || 0), 0),
    0
  );
  const designAddon = includesDesign ? DESIGN_FEE : 0;
  const deliveryAddon = fulfillment === "delivery" ? DELIVERY_FEE : 0;
  const grandTotal = treatsTotal + designAddon + deliveryAddon;
  const selectedCount = Object.keys(selection).length;

  const validate = () => {
    const eObj = {};
    if (!date) eObj.date = "Please pick a date";
    if (!form.event_type) eObj.event_type = "Please pick an event type";
    if (selectedCount === 0) eObj.form = "Please pick at least one treat, its flavor(s), and quantity.";
    if (fulfillment === "delivery" && !deliveryAddress.trim()) {
      eObj.delivery_address = "Please provide a delivery address.";
    }
    Object.entries(selection).forEach(([id, sel]) => {
      const flavorCount = Object.keys(sel.flavors).length;
      if (flavorCount === 0) eObj[id] = `Pick at least one flavor for ${sel.menu_item_name}.`;
    });
    setErrors(eObj);
    return Object.keys(eObj).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required flavor and quantity picks.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        guest_count: parseInt(form.guest_count, 10) || 0,
        event_date: format(date, "yyyy-MM-dd"),
        items: buildItemsPayload(),
        fulfillment,
        delivery_address: fulfillment === "delivery" ? deliveryAddress.trim() : "",
        includes_design: includesDesign,
        design_reference_url: includesDesign ? designReferenceUrl : "",
      });
      setBooking(data);
      toast.success("Booking request received!");
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail;
      if (status === 409) {
        toast.error(msg || "That date is no longer available. Please pick another.");
        try {
          const { data: bd } = await api.get("/blocked-dates");
          setBlockedSet(new Set(bd?.dates || []));
        } catch (e2) { console.error("blocked-dates refresh failed", e2); }
        setDate(null);
      } else if ((status === 422 || status === 400) && fulfillment === "delivery" && typeof msg === "string") {
        toast.error(msg);
        setErrors((er) => ({ ...er, delivery_address: msg }));
      } else {
        toast.error(msg?.[0]?.msg || msg || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setBooking(null); setDate(null); setSelection({}); setErrors({});
    setFulfillment("pickup"); setDeliveryAddress(""); setIncludesDesign(false);
    setDesignReferenceUrl("");
    setForm({ name: "", email: "", phone: "", event_type: "", guest_count: 20, notes: "" });
  };

  if (booking) {
    const treatsBookingTotal = (booking.items || []).reduce(
      (sum, it) => sum + (it.flavors || []).reduce((a, f) => a + f.quantity * (f.price_per_dozen || 0), 0),
      0
    );
    const bookingDesignFee = booking.design_fee || (booking.includes_design ? DESIGN_FEE : 0);
    const bookingDeliveryFee = booking.delivery_fee || (booking.fulfillment === "delivery" ? DELIVERY_FEE : 0);
    const bookingTotal = treatsBookingTotal + bookingDesignFee + bookingDeliveryFee;
    return (
      <section id="book" data-testid={HOME.bookingSection} className="max-w-2xl mx-auto px-6 py-20">
        <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_20px_50px_-20px_rgba(20,184,166,0.2)] p-8 sm:p-10">
          <div className="text-center mb-8">
            <div className="mx-auto h-14 w-14 rounded-full bg-sky-50 grid place-items-center mb-4">
              <Check className="h-6 w-6 text-blue-800" />
            </div>
            <h3 className="font-serif-display text-3xl text-stone-900 mb-2">{c.booking_success_title || "Request received!"}</h3>
            <p className="text-stone-600">
              {(c.booking_success_text || "Thank you, {name}. Your order is pending approval — we'll review the details and get back to you within 24-48 hours to confirm and share deposit info.").replace("{name}", booking.name)}
            </p>
          </div>

          <div className="rounded-2xl border border-stone-100 bg-stone-50/40 p-5 mb-6" data-testid="order-card">
            <p className="text-xs uppercase tracking-widest text-amber-500 mb-3">Your order</p>
            <dl className="grid grid-cols-3 gap-y-2 text-sm mb-5">
              <dt className="text-stone-500">Name</dt><dd className="col-span-2 text-stone-800">{booking.name}</dd>
              <dt className="text-stone-500">Event</dt><dd className="col-span-2 text-stone-800">{booking.event_type} · {booking.event_date}</dd>
              <dt className="text-stone-500">Guests</dt><dd className="col-span-2 text-stone-800">{booking.guest_count}</dd>
              {booking.notes && (<>
                <dt className="text-stone-500">Notes</dt><dd className="col-span-2 text-stone-800">{booking.notes}</dd>
              </>)}
            </dl>
            <div className="border-t border-stone-100 pt-4 space-y-3">
              {(booking.items || []).map((it, idx) => {
                const itemTotal = (it.flavors || []).reduce((a, f) => a + f.quantity * (f.price_per_dozen || 0), 0);
                return (
                  <div key={`${it.menu_item_id || it.menu_item_name}-${idx}`}>
                    <div className="flex justify-between items-baseline">
                      <p className="font-medium text-stone-800">{it.menu_item_name}</p>
                      <p className="text-sm text-stone-600">{money(itemTotal)}</p>
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {(it.flavors || []).map((f, i) => (
                        <li key={`${f.flavor}-${i}`} className="text-sm text-stone-600 flex justify-between">
                          <span>{f.flavor} <span className="text-stone-400">· {f.quantity} doz</span></span>
                          <span className="text-stone-500">{money(f.quantity * (f.price_per_dozen || 0))}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            {(bookingDesignFee > 0 || bookingDeliveryFee > 0) && (
              <div className="border-t border-stone-100 mt-4 pt-3 space-y-1 text-sm">
                {bookingDesignFee > 0 && (
                  <div className="flex justify-between text-stone-700" data-testid="order-card-design">
                    <span className="flex items-center gap-1.5"><Palette className="h-3.5 w-3.5 text-amber-500" />Custom design</span>
                    <span>{money(bookingDesignFee)}</span>
                  </div>
                )}
                {booking.design_reference_url && (
                  <div className="flex items-center gap-3 rounded-xl bg-white border border-amber-100 p-2" data-testid="order-card-design-reference">
                    <img src={resolveImageUrl(booking.design_reference_url)} alt="Reference" className="h-12 w-12 rounded-lg object-cover" />
                    <span className="text-xs text-stone-500">Design reference attached</span>
                  </div>
                )}
                {bookingDeliveryFee > 0 ? (
                  <div className="flex justify-between text-stone-700" data-testid="order-card-delivery">
                    <span className="flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-blue-900" />Delivery{booking.delivery_address ? ` — ${booking.delivery_address}` : ""}</span>
                    <span>{money(bookingDeliveryFee)}</span>
                  </div>
                ) : booking.fulfillment === "pickup" && (
                  <div className="flex justify-between text-stone-700" data-testid="order-card-pickup">
                    <span className="flex items-center gap-1.5"><Store className="h-3.5 w-3.5 text-blue-900" />Pickup</span>
                    <span className="text-stone-400">Free</span>
                  </div>
                )}
              </div>
            )}
            <div className="border-t border-stone-200 mt-4 pt-4 flex justify-between items-baseline">
              <span className="font-serif-display text-lg text-stone-900">Estimated total</span>
              <span className="font-serif-display text-2xl text-blue-800" data-testid="order-card-total">{money(bookingTotal)}</span>
            </div>
            <p className="text-xs text-stone-400 mt-2">Final quote may include tax. You&apos;ll receive the exact amount with your approval.</p>
          </div>

          <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-4 mb-6 flex items-start gap-3" data-testid="pending-approval-notice">
            <Clock className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-700">Pending approval</p>
              <p className="text-xs text-stone-600 mt-1">No deposits or payments are accepted until your order is approved. We&apos;ll email/text you within 24-48 hours with confirmation and payment details.</p>
            </div>
          </div>

          <Button
            data-testid={BOOKING.skipDeposit}
            variant="outline"
            onClick={resetForm}
            className="btn-pill bg-white border border-stone-200 text-stone-700 w-full"
          >
            Submit another request
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section id="book" data-testid={HOME.bookingSection} className="max-w-3xl mx-auto px-6 py-20">
      <div className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-amber-500 mb-2 inline-flex items-center gap-2 justify-center">
          <Sparkles className="h-3 w-3" /> {c.booking_eyebrow || "Book Your Sweets"}
        </p>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-stone-900 mb-3">{c.booking_heading || "Let's plan your event."}</h2>
        <p className="text-stone-500">{c.booking_description || "Share the details and we'll craft a custom quote for you."}</p>
      </div>

      <form onSubmit={submit} data-testid={BOOKING.form} className="bg-white rounded-3xl border border-stone-100 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.08)] p-8 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full name">
            <Input data-testid={BOOKING.name} required value={form.name} onChange={(e) => setField("name", e.target.value)} className="rounded-lg" />
          </Field>
          <Field label="Email">
            <Input data-testid={BOOKING.email} required type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className="rounded-lg" />
          </Field>
          <Field label="Phone">
            <Input data-testid={BOOKING.phone} required value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="rounded-lg" />
          </Field>
          <Field label="Guest count">
            <Input data-testid={BOOKING.guests} required type="number" min="1" value={form.guest_count} onChange={(e) => setField("guest_count", e.target.value)} className="rounded-lg" />
          </Field>
          <Field label="Event date">
            <Popover>
              <PopoverTrigger asChild>
                <Button data-testid={BOOKING.date} type="button" variant="outline" className="w-full justify-start rounded-lg font-normal text-left">
                  <CalendarDays className="mr-2 h-4 w-4 text-stone-500" />
                  {date ? format(date, "PPP") : <span className="text-stone-400">Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  initialFocus
                  disabled={dateDisabled}
                  components={dateComponents}
                />
              </PopoverContent>
            </Popover>
          </Field>
          <Field label="Event type">
            <Select value={form.event_type} onValueChange={(v) => setField("event_type", v)}>
              <SelectTrigger data-testid={BOOKING.eventType} className="rounded-lg">
                <SelectValue placeholder="Choose event" />
              </SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="pt-4 border-t border-stone-100">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-medium text-stone-800">
              Pick your treats & flavors <span className="text-amber-500">*</span>
            </Label>
            <span className="text-xs text-stone-400" data-testid="booking-totals-summary">
              {selectedCount} treat{selectedCount === 1 ? "" : "s"} · {totalDozens} doz · <span className="text-blue-800 font-semibold">{money(treatsTotal)}</span>
            </span>
          </div>
          <p className="text-xs text-stone-500 mb-4">Choose one or more treats. For each, pick at least one flavor and set how many <span className="font-medium">dozens</span> you want (up to 8 dozens per flavor).</p>

          {menu.length === 0 ? (
            <p className="text-sm text-stone-500">Menu is loading…</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {menu.map((item) => (
                <MenuOrderCard
                  key={item.id}
                  item={item}
                  selection={selection[item.id]}
                  onToggleFlavor={(flavorObj) => toggleFlavor(item, flavorObj)}
                  onSetQty={(flavor, qty) => setFlavorQty(item, flavor, qty)}
                  onRemove={() => removeItem(item.id)}
                  error={errors[item.id]}
                />
              ))}
            </div>
          )}
          {selectedCount > 0 && (
            <div className="mt-4 rounded-2xl bg-sky-50/60 border border-sky-100 px-4 py-3 flex items-baseline justify-between" data-testid="booking-grand-total">
              <span className="text-sm text-stone-700">Treats subtotal <span className="text-xs text-stone-400">(before design / delivery / tax)</span></span>
              <span className="font-serif-display text-2xl text-blue-900">{money(treatsTotal)}</span>
            </div>
          )}
          {errors.form && <p className="text-sm text-amber-600 mt-3">{errors.form}</p>}
        </div>

        <div className="pt-4 border-t border-stone-100 space-y-4">
          <div>
            <Label className="text-sm font-medium text-stone-800 mb-2 block">How would you like it? <span className="text-amber-500">*</span></Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="booking-fulfillment">
              <FulfillmentOption
                testid="booking-fulfillment-pickup"
                active={fulfillment === "pickup"}
                onSelect={() => setFulfillment("pickup")}
                icon={Store}
                title="Pickup"
                subtitle="Free"
                blurb="Grab your order from us."
                accent="teal"
              />
              <FulfillmentOption
                testid="booking-fulfillment-delivery"
                active={fulfillment === "delivery"}
                onSelect={() => setFulfillment("delivery")}
                icon={Truck}
                title="Delivery"
                subtitle={`+${money(DELIVERY_FEE)}`}
                blurb="We'll bring it right to your event."
                accent="pink"
              />
            </div>
            {fulfillment === "delivery" && (
              <div className="mt-3">
                <Label className="text-xs text-stone-600 mb-1 block">Delivery address</Label>
                <Textarea
                  data-testid="booking-delivery-address"
                  rows={2}
                  value={deliveryAddress}
                  onChange={(e) => { setDeliveryAddress(e.target.value); if (errors.delivery_address) setErrors((er) => ({ ...er, delivery_address: null })); }}
                  placeholder="Street, city, ZIP"
                  className="rounded-lg"
                />
                <p className="text-xs text-stone-400 mt-1" data-testid="booking-delivery-radius-hint">
                  We deliver within {DELIVERY_MAX_MILES} miles of our kitchen. Include street, city, and ZIP for the best match.
                </p>
                {errors.delivery_address && <p className="text-xs text-amber-600 mt-1" data-testid="booking-delivery-address-error">{errors.delivery_address}</p>}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIncludesDesign((v) => !v)}
            data-testid="booking-design-toggle"
            aria-pressed={includesDesign}
            className={`w-full text-left rounded-2xl border p-4 transition-all flex items-center gap-3 ${includesDesign ? "border-amber-300 bg-amber-50/50 shadow-[0_10px_30px_-15px_rgba(236,72,153,0.25)]" : "border-stone-200 bg-white hover:border-amber-200"}`}
          >
            <div className={`h-10 w-10 rounded-full grid place-items-center shrink-0 ${includesDesign ? "bg-amber-500 text-white" : "bg-amber-50 text-amber-500"}`}>
              <Palette className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-stone-900 flex items-center gap-2">
                Add custom design
                <span className="text-xs font-normal text-amber-600 bg-amber-100 rounded-full px-2 py-0.5">+{money(DESIGN_FEE)}</span>
              </p>
              <p className="text-xs text-stone-500 mt-0.5">Themed decoration, colors, and personalization for your treats.</p>
            </div>
            <span className={`h-5 w-5 rounded-full border-2 grid place-items-center shrink-0 ${includesDesign ? "border-amber-500 bg-amber-500" : "border-stone-300"}`}>
              {includesDesign && <Check className="h-3 w-3 text-white" />}
            </span>
          </button>

          {includesDesign && (
            <div className="rounded-2xl border border-amber-100 bg-amber-50/30 p-4 space-y-4" data-testid="booking-design-panel">
              <div>
                <p className="text-sm font-medium text-stone-800 mb-2">Reference photo <span className="text-stone-400 font-normal">(optional)</span></p>
                <p className="text-xs text-stone-500 mb-3">Pick from our past designs, or upload your own inspiration.</p>

                {designGallery.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-design-gallery">
                    {designGallery.map((url) => {
                      const isPicked = designReferenceUrl === url;
                      return (
                        <button
                          type="button"
                          key={url}
                          onClick={() => setDesignReferenceUrl(isPicked ? "" : url)}
                          data-testid="booking-design-gallery-item"
                          className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${isPicked ? "border-amber-500 ring-2 ring-amber-200" : "border-transparent hover:border-amber-200"}`}
                        >
                          <img src={resolveImageUrl(url)} alt="Reference" className="h-full w-full object-cover" />
                          {isPicked && (
                            <span className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-amber-500 text-white grid place-items-center shadow">
                              <Check className="h-3.5 w-3.5" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="mt-3">
                  <p className="text-xs uppercase tracking-widest text-stone-400 mb-2">Or upload your own</p>
                  <ImageUploader
                    value={designGallery.includes(designReferenceUrl) ? "" : designReferenceUrl}
                    onChange={(url) => setDesignReferenceUrl(url || "")}
                    endpoint="/design-inspiration/upload"
                    testId="booking-design-inspiration-uploader"
                  />
                </div>

                {designReferenceUrl && (
                  <div className="mt-3 flex items-center gap-3 rounded-xl bg-white border border-amber-100 p-2" data-testid="booking-design-reference-preview">
                    <img src={resolveImageUrl(designReferenceUrl)} alt="Chosen reference" className="h-14 w-14 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-stone-500">Reference selected</p>
                      <p className="text-sm text-stone-700 truncate">{designReferenceUrl.split("/").pop()}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDesignReferenceUrl("")}
                      data-testid="booking-design-reference-clear"
                      className="text-xs text-stone-400 hover:text-red-500 flex items-center gap-1"
                    >
                      <Trash2 className="h-3 w-3" /> Remove
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedCount > 0 && (designAddon > 0 || deliveryAddon > 0) && (
            <div className="rounded-2xl border border-stone-100 bg-stone-50/40 p-4 space-y-1 text-sm" data-testid="booking-addons-summary">
              <div className="flex justify-between text-stone-600">
                <span>Treats subtotal</span>
                <span>{money(treatsTotal)}</span>
              </div>
              {designAddon > 0 && (
                <div className="flex justify-between text-stone-600" data-testid="booking-summary-design">
                  <span>Custom design</span>
                  <span>{money(designAddon)}</span>
                </div>
              )}
              {deliveryAddon > 0 && (
                <div className="flex justify-between text-stone-600" data-testid="booking-summary-delivery">
                  <span>Delivery</span>
                  <span>{money(deliveryAddon)}</span>
                </div>
              )}
              <div className="flex justify-between items-baseline pt-2 border-t border-stone-100">
                <span className="font-serif-display text-lg text-stone-900">Estimated total</span>
                <span className="font-serif-display text-2xl text-blue-900" data-testid="booking-summary-total">{money(grandTotal)}</span>
              </div>
            </div>
          )}
        </div>

        <Field label="Notes (theme, colors, allergies)">
          <Textarea data-testid={BOOKING.notes} rows={4} value={form.notes} onChange={(e) => setField("notes", e.target.value)} className="rounded-lg" />
        </Field>

        <Button
          data-testid={BOOKING.submit}
          type="submit"
          disabled={loading}
          className="btn-pill btn-navy w-full h-12 text-base border-0"
        >
          {loading ? "Sending..." : (c.booking_submit_label || "Submit Booking Request")}
        </Button>
      </form>
    </section>
  );
}

function MenuOrderCard({ item, selection, onToggleFlavor, onSetQty, onRemove, error }) {
  const selectedFlavors = selection?.flavors || {};
  const active = Object.keys(selectedFlavors).length > 0;
  const flavors = item.flavors || [];
  const itemSubtotal = Object.values(selectedFlavors).reduce(
    (a, m) => a + (m.quantity || 0) * (m.price_per_dozen || 0),
    0
  );

  return (
    <div
      data-testid={`booking-menu-item-${item.id}`}
      className={`rounded-2xl border p-4 transition-all ${active ? "border-amber-300 bg-amber-50/40 shadow-[0_10px_30px_-15px_rgba(236,72,153,0.25)]" : "border-stone-200 bg-white"} ${error ? "!border-red-300" : ""}`}
    >
      <div className="flex gap-3">
        <div className="h-16 w-16 rounded-xl overflow-hidden shrink-0 bg-amber-100 grid place-items-center">
          {item.image_url ? (
            <img src={resolveImageUrl(item.image_url)} alt={item.name} className="h-full w-full object-cover" />
          ) : (
            <span className="text-amber-300 text-2xl">🧁</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="font-medium text-stone-900 truncate">{item.name}</p>
            {active ? (
              <button
                type="button"
                onClick={onRemove}
                className="text-stone-400 hover:text-red-500 text-xs flex items-center gap-1"
                data-testid={`booking-remove-${item.id}`}
              >
                <Trash2 className="h-3 w-3" /> Clear
              </button>
            ) : (
              item.price_from > 0 && <p className="text-xs text-stone-500">from {money(item.price_from)}/dz</p>
            )}
          </div>
          {active && (
            <p className="text-xs text-blue-900 mt-0.5 font-medium" data-testid={`booking-item-subtotal-${item.id}`}>
              Subtotal: {money(itemSubtotal)}
            </p>
          )}
        </div>
      </div>

      {flavors.length === 0 ? (
        <p className="mt-3 text-xs text-stone-400 italic">The owner hasn&apos;t added flavors for this item yet. Please contact them directly.</p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {flavors.map((flv) => {
              const name = flavorKey(flv);
              const price = flavorPrice(flv);
              const notice = flavorNotice(flv);
              const isOn = selectedFlavors[name] != null;
              return (
                <div key={name} className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => onToggleFlavor(flv)}
                    data-testid={`booking-flavor-${item.id}-${name.replace(/[^a-z0-9]+/gi, "_")}`}
                    className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${isOn ? "bg-amber-500 text-white border-amber-500" : "bg-white border-stone-200 text-stone-600 hover:border-amber-300"}`}
                  >
                    {name} {price > 0 && <span className={isOn ? "text-amber-100" : "text-stone-400"}>· {money(price)}</span>}
                  </button>
                  {notice && (
                    <span
                      data-testid={`booking-flavor-notice-${item.id}-${name.replace(/[^a-z0-9]+/gi, "_")}`}
                      className="mt-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-1.5 py-0.5 flex items-center gap-1 self-start"
                    >
                      <span aria-hidden>⚠</span> {notice}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {active && (
            <div className="mt-3 space-y-2">
              {Object.entries(selectedFlavors).map(([flavor, meta]) => {
                const rowTotal = meta.quantity * (meta.price_per_dozen || 0);
                return (
                  <div key={flavor} className="flex items-center gap-2 rounded-xl bg-white border border-stone-200 px-2 py-1.5">
                    <span className="text-xs text-stone-700 flex-1 truncate">
                      {flavor}
                      {meta.price_per_dozen > 0 && <span className="text-stone-400 ml-1">· {money(meta.price_per_dozen)}/dz</span>}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => onSetQty(flavor, meta.quantity - 1)}
                        className="h-7 w-7 grid place-items-center rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600"
                        data-testid={`booking-qty-dec-${item.id}-${flavor.replace(/[^a-z0-9]+/gi, "_")}`}
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <input
                        type="number"
                        min="1"
                        max="8"
                        value={meta.quantity}
                        onChange={(e) => onSetQty(flavor, parseInt(e.target.value, 10) || 1)}
                        className="w-12 h-7 text-center text-sm rounded-md border border-stone-200 focus:outline-none focus:ring-2 focus:ring-amber-300"
                        data-testid={`booking-qty-input-${item.id}-${flavor.replace(/[^a-z0-9]+/gi, "_")}`}
                      />
                      <span className="text-xs text-stone-400 mr-1">dz</span>
                      <button
                        type="button"
                        onClick={() => onSetQty(flavor, meta.quantity + 1)}
                        className="h-7 w-7 grid place-items-center rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600"
                        data-testid={`booking-qty-inc-${item.id}-${flavor.replace(/[^a-z0-9]+/gi, "_")}`}
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    {rowTotal > 0 && <span className="text-xs text-stone-500 w-14 text-right tabular-nums">{money(rowTotal)}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm text-stone-700 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function FulfillmentOption({ testid, active, onSelect, icon: Icon, title, subtitle, blurb, accent }) {
  const ring = active
    ? accent === "pink"
      ? "border-amber-300 bg-amber-50/60 shadow-[0_10px_30px_-15px_rgba(236,72,153,0.3)]"
      : "border-blue-300 bg-sky-50/60 shadow-[0_10px_30px_-15px_rgba(20,184,166,0.3)]"
    : "border-stone-200 bg-white hover:border-stone-300";
  const iconBg = active
    ? accent === "pink" ? "bg-amber-500 text-white" : "bg-blue-900 text-white"
    : accent === "pink" ? "bg-amber-50 text-amber-500" : "bg-sky-50 text-blue-900";
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={testid}
      aria-pressed={active}
      className={`text-left rounded-2xl border p-4 transition-all flex items-center gap-3 ${ring}`}
    >
      <div className={`h-10 w-10 rounded-full grid place-items-center shrink-0 ${iconBg}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <p className="font-medium text-stone-900 flex items-center gap-2">
          {title}
          <span className={`text-xs font-normal rounded-full px-2 py-0.5 ${accent === "pink" ? "bg-amber-100 text-amber-600" : "bg-sky-100 text-blue-900"}`}>{subtitle}</span>
        </p>
        <p className="text-xs text-stone-500 mt-0.5">{blurb}</p>
      </div>
      <span className={`h-5 w-5 rounded-full border-2 grid place-items-center shrink-0 ${active ? (accent === "pink" ? "border-amber-500 bg-amber-500" : "border-blue-900 bg-blue-900") : "border-stone-300"}`}>
        {active && <Check className="h-3 w-3 text-white" />}
      </span>
    </button>
  );
}
